import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import Info from "./info";
import form from "./schemas/form";
import initialValues from "./schemas/initialValues";
import { PaymentSalarySheetService } from "services/payment-salary-sheet";

import { getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  fullName: "",
  rank: "",
  coeff: "",
  prodQty: "",
  prodMoney: "",
  timeDays: "",
  timeMoney: "",
  leaveDays: "",
  leaveMoney: "",
  allowanceSalary: "",
  allowanceOther: "",
  advance: "",
  bhxh: "",
  deductOther: "",
  tax: "",
});

function PayrollPaymentSheetDetail() {
  const { formId, formField } = form;
  const { id } = useParams();
  const location = useLocation();
  const containEdit = location.pathname?.includes("/edit");
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [data, setData] = useState({});

  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await PaymentSalarySheetService.getById(id);
        const apiData = response.data ? response.data : response;

        const mappedRows = (apiData.employerSalaries || []).map((emp) => ({
          fullName: emp.name || "",
          rank: emp.salaryLevel || "",
          coeff: emp.coefficient || "",
          prodQty: emp.productSalary?.totalProduct || "",
          prodMoney: emp.productSalary?.totalSalary || "",
          timeDays: emp.timeSalary?.totalTime || "",
          timeMoney: emp.timeSalary?.totalSalary || "",
          leaveDays: emp.paidLeave?.totalTime || "",
          leaveMoney: emp.paidLeave?.totalSalary || "",
          allowanceSalary: emp.allowance || "",
          allowanceOther: emp.otherAllowance || "",
          advance: emp.advanceI || "",
          bhxh: emp.deduction?.bhxh || "",
          tax: emp.deduction?.personalIncomeTax || "",
          deductOther: emp.deduction?.other || "",
          netPay: emp.advanceII?.total || "",
        }));

        const loadedData = {
          ...initialValues,
          day: apiData.day || "",
          month: apiData.month || "",
          year: apiData.year || "",
          unit: apiData.unit || "",
          department: apiData.department || "",
          docNumber: apiData.unitNumber || "",
          salaryRows: mappedRows.length > 0 ? mappedRows : [createRow()],
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi getById", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const onChangeValue = useCallback((type, value) => {
    setData((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleBack = () => navigation(-1);

  const submitForm = async (values) => {
    try {
      if (!id) {
        await PaymentSalarySheetService.create(values);
        toast.success("Tạo phiếu lương thành công!");
      } else {
        await PaymentSalarySheetService.update(id, values);
        toast.success("Cập nhật phiếu lương thành công!");
      }
    } catch (error) {
      console.error("Lỗi khi gọi API lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = (values) => {
    const today = new Date();
    const finalUnit = data.unit !== undefined ? data.unit : values.unit;
    const finalDepartment = data.department !== undefined ? data.department : values.department;
    const finalDocNumber = data.docNumber !== undefined ? data.docNumber : values.docNumber;
    const finalRows = data.salaryRows !== undefined ? data.salaryRows : values.salaryRows || [];
    const finalTotals = data.totals !== undefined ? data.totals : values.totals || {};

    const headerInfo = {
      unit: finalUnit,
      department: finalDepartment,
      docNumber: finalDocNumber,
      month: data.month || values.month || today.getMonth() + 1,
      year: data.year || values.year || today.getFullYear(),
      day: data.day || values.day || today.getDate(),
    };

    const htmlContent = getPrintTemplate({
      headerInfo,
      rows: finalRows,
      totals: finalTotals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSubmit = async (values, actions) => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    const finalUnit = data.unit !== undefined ? data.unit : values.unit;
    const finalDepartment = data.department !== undefined ? data.department : values.department;
    const finalDocNumber = data.docNumber !== undefined ? data.docNumber : values.docNumber;
    const finalRows = data.salaryRows !== undefined ? data.salaryRows : values.salaryRows || [];
    const finalTotalNetPay =
      data.totals?.netPay !== undefined ? data.totals.netPay : values.totals?.netPay || 0;

    const num = (val) => (val && !isNaN(val) ? Number(val) : 0);

    const employers = finalRows.map((item) => {
      return {
        name: item.fullName || "bug",
        salaryLevel: item.rank || "",
        coefficient: num(item.coeff),

        productSalary: {
          totalProduct: num(item.prodQty),
          totalSalary: num(item.prodMoney),
        },
        timeSalary: {
          totalTime: num(item.timeDays),
          totalSalary: num(item.timeMoney),
        },
        paidLeave: {
          totalTime: num(item.leaveDays),
          totalSalary: num(item.leaveMoney),
        },

        allowance: num(item.allowanceSalary),
        otherAllowance: num(item.allowanceOther),
        total: num(item.totalGross),

        advanceI: num(item.advance),

        deduction: {
          bhxh: num(item.bhxh),
          personalIncomeTax: num(item.tax),
          other: num(item.deductOther),
          total: num(item.totalDeduct),
        },

        advanceII: {
          total: num(item.netPay),
          note: "",
          signature: "",
        },
      };
    });

    const payload = {
      year: data.year || currentYear,
      month: data.month || currentMonth,
      day: data.day || today.getDate(),
      unitNumber: finalDocNumber || "",
      unit: finalUnit || "",
      department: finalDepartment || "",
      totalSalary: finalTotalNetPay,
      totalEmployment: employers.length,
      employers,
    };

    try {
      await submitForm(payload);
    } catch (error) {
      console.error("Lỗi khi gọi API lưu:", error);
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form id={formId} autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <Info
                          formData={{
                            values,
                            touched,
                            formField,
                            errors,
                            getData,
                            onChangeValue,
                            setFieldValue,
                          }}
                        />
                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Bảng Lương
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu" : "Tạo"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default PayrollPaymentSheetDetail;
